// Reusable SEO component for managing page-specific meta tags.
// Auto-detects the current route and resolves its SEO via getSEOConfig
// (which reads each route's inline `seo` block in pageRoutes.ts).
//
// React 19 hoists <title>/<meta>/<link> into <head> automatically; the
// React-rendered <title> is inserted before the static index.html fallback,
// so it takes precedence. The JSON-LD <script> is not hoisted (React only
// hoists async src scripts) and renders in the body, which is valid
// placement for structured data.

import React from 'react';
import { useLocation } from 'react-router';
import { useRoute } from '../hooks/useRoute';
import { buildGraph } from './jsonld';
import { resolveSEO } from './resolveSEO';
import type { SEOProps } from './types';

const SEO: React.FC<SEOProps> = (props) => {
  const location = useLocation();
  const route = useRoute();

  const resolved = resolveSEO({
    props,
    pathname: location.pathname,
    normalizedPath: route.normalizedPath,
  });

  const graph = buildGraph(resolved, props);
  const type = props.type ?? 'website';
  const { title, description, image, url } = resolved;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Structured Data (JSON-LD) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(graph) }}
      />

      {/* Open Graph / Facebook / Messaging Apps (iMessage, WhatsApp, Slack, etc.) */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Airhead Games" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </>
  );
};

export default SEO;

export type { ArticleData, BreadcrumbItem, GameData, SEOProps } from './types';
